import { useEffect, useState } from 'react';

import { DownOutlined, PartitionOutlined, RightOutlined, UpOutlined } from '@ant-design/icons';
import { Button, Flex } from '@revenuemarket/cds';
import { colorTokens } from '@revenuemarket/design';
import { overlays } from '@revenuemarket/overlays';
import { FormattedInput } from '@revenuemarket/ui/FormattedInput';
import { Form, Tooltip, Typography } from 'antd';

import { LabeledBankAccountTransaction } from '~apis/cash-flow-overview/transactions/getTransactions';
import TransactionMappingModal from '~components/Mapping/TransactionMappingModal';
import { css } from '~libs/emotion';

import SettlementCompletedButton from './SettlementCompletedButton';
import SettlementListDrawerWrapper from './SettlementListDrawerWrapper';
import { MappingTransaction } from './SettlementMappingStore';

interface Props {
  onSelect: (mappingPrice: number) => void;
  status: 'NOT_SELECTED' | 'SELECTED' | 'COMPLETED';
  item: LabeledBankAccountTransaction;
  mappingTransactions: MappingTransaction[];
  originalMappingTransactions: MappingTransaction[];
}

const SettlementItemActionBtn: React.FC<Props> = ({
  status,
  onSelect,
  item,
  mappingTransactions,
}) => {
  const [price, setPrice] = useState<number | null>(
    item?.labelKrwAmount ? Math.abs(item?.labelKrwAmount) : null,
  );

  const [open, setOpen] = useState<boolean>(false);

  const splittedLabelInfo = item.allLabelIds || [];
  const splitDisabled = splittedLabelInfo.some((labelId) =>
    mappingTransactions.map((m) => m.labelId).includes(labelId),
  );

  useEffect(() => {
    if (item.labelKrwAmount) {
      setPrice(Math.abs(item.labelKrwAmount ?? 0));
    }
  }, [item.labelKrwAmount]);

  if (item.labelCount > 1) {
    return (
      <Flex gap={10} align="center">
        <Tooltip
          title={
            splitDisabled ? '현재 정산 내역에 선택되어 있어 분할 내역을 편집할 수 없습니다.' : ''
          }
        >
          <Button
            disabled={splitDisabled}
            onClick={(e) => {
              e.stopPropagation();
              overlays.open(({ isOpen, onClose }) => (
                <TransactionMappingModal
                  open={isOpen}
                  onCancel={onClose}
                  transactionId={item.transactionId}
                  showTaxInvoice={false}
                />
              ));
            }}
            icon={<PartitionOutlined />}
            style={{
              width: 160,
            }}
          >
            분할 내역 편집
          </Button>
        </Tooltip>
        <Button
          className="ant-table-row-expand-icon-cell"
          onClick={() => {
            setOpen(!open);
          }}
        >
          <Flex align="center" gap={8}>
            <Typography.Text>{open ? '닫기' : '열기'}</Typography.Text>
            {open ? <UpOutlined /> : <DownOutlined />}
          </Flex>
        </Button>
      </Flex>
    );
  }

  if (status === 'COMPLETED') {
    return (
      <Flex
        style={{
          width: '100%',
        }}
        align="center"
      >
        <div
          style={{
            width: 160,
            marginRight: 10,
          }}
        ></div>
        <SettlementCompletedButton
          style={{
            width: 64,
            textAlign: 'center',
          }}
          onClick={() => {
            overlays.open(({ isOpen, onClose }) => (
              <SettlementListDrawerWrapper
                transactionId={item.transactionId}
                labelId={item.labelId}
                open={isOpen}
                onClose={onClose}
              />
            ));
          }}
        />
      </Flex>
    );
  }

  if (status === 'SELECTED') {
    return (
      <Flex gap={10} align="center">
        <Typography.Text
          style={{
            color: colorTokens.textDescription,
            width: 160,
            textAlign: 'right',
          }}
        >
          {mappingTransactions
            .find((m) => m.transactionId === item.transactionId)
            ?.mappingAmount.toLocaleString()}
          원
        </Typography.Text>
        <Typography.Text
          style={{
            color: colorTokens.textDescription,
            width: 64,
            textAlign: 'center',
          }}
        >
          선택됨
        </Typography.Text>
      </Flex>
    );
  }

  const selected = mappingTransactions.some(
    (mappingTransaction) =>
      mappingTransaction.transactionId === item.transactionId &&
      mappingTransaction.labelId === item.labelId,
  );

  const triggerPriceValidation = price !== null && price > Math.abs(item?.labelKrwAmount ?? 0);

  return (
    <Form.Item
      style={{
        marginBottom: 0,
      }}
      validateStatus={triggerPriceValidation ? 'error' : undefined}
      help={triggerPriceValidation ? '왼쪽 금액보다 작거나 같은 금액을 입력해주세요.' : undefined}
    >
      <Flex gap={10} align="center">
        {status !== 'NOT_SELECTED' ? (
          <Typography.Text
            style={{
              width: 160,
              color: colorTokens.textDisabled,
            }}
          >
            {price === null ? '-' : price.toLocaleString() + '원'}
          </Typography.Text>
        ) : (
          <FormattedInput
            style={{
              width: 160,
            }}
            className={css`
              & input {
                text-align: right;
              }
            `}
            value={price + ''}
            onChange={(value) => {
              setPrice(Number(value));
            }}
            suffix="원"
            formatter={(value: string) => {
              if (!value || isNaN(parseInt(value, 10))) return '';
              return parseInt(value, 10).toLocaleString();
            }}
            deformatter={(value: string) => {
              if (!value || isNaN(parseInt(value, 10))) return '';
              return value.replace(/,/g, '');
            }}
          />
        )}
        <Button
          onClick={() => {
            if (price === null) {
              return;
            }
            onSelect(price);
          }}
          disabled={selected || triggerPriceValidation || price === null}
        >
          <Flex align="center" gap={8}>
            <Typography.Text
              style={{
                color: selected || triggerPriceValidation ? colorTokens.textDisabled : '',
              }}
            >
              {selected ? '선택됨' : '선택'}
            </Typography.Text>
            <RightOutlined />
          </Flex>
        </Button>
      </Flex>
    </Form.Item>
  );
};

export default SettlementItemActionBtn;
